import { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { api, type CreateVendorResponse } from '@/lib/api';
import { useModalDismiss } from '@/lib/use-modal-dismiss';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function NewVendorModal({ open, onClose }: Props) {
  const dismiss = useModalDismiss(onClose);
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [, setLocation] = useLocation();

  const [legalName, setLegalName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [reliabilityRating, setReliabilityRating] = useState<number | ''>('');
  const [aogCapable, setAogCapable] = useState(false);
  const [brokerFriendly, setBrokerFriendly] = useState(true);
  const [paymentTerms, setPaymentTerms] = useState('');
  const [preferredCurrency, setPreferredCurrency] = useState('USD');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) {
      setLegalName('');
      setDisplayName('');
      setReliabilityRating('');
      setAogCapable(false);
      setBrokerFriendly(true);
      setPaymentTerms('');
      setPreferredCurrency('USD');
      setNotes('');
    }
  }, [open]);

  const createMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        legalName,
        brokerFriendly,
        aogCapable,
      };
      if (displayName) body.displayName = displayName;
      if (reliabilityRating !== '') body.reliabilityRating = reliabilityRating;
      if (paymentTerms) body.paymentTerms = paymentTerms;
      if (preferredCurrency) body.preferredCurrency = preferredCurrency;
      if (notes) body.notes = notes;
      return api<CreateVendorResponse>('/api/vendors', {
        method: 'POST',
        tokenGetter: () => getToken(),
        idempotencyKey: crypto.randomUUID(),
        body: JSON.stringify(body),
      });
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['vendors'] });
      setLocation(`/vendors/${data.vendor.id}`);
      onClose();
    },
  });

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      {...dismiss}
    >
      <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[90dvh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <h2 className="font-semibold">New vendor</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1 -mr-1" aria-label="Close">✕</button>
        </div>

        <div className="p-5 space-y-3 flex-1 overflow-auto">
          <label className="block">
            <div className="text-xs font-medium text-muted-foreground mb-1">Legal name *</div>
            <input
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              placeholder="ABC Aviation Supply, LLC"
              maxLength={200}
              className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
            />
          </label>
          <label className="block">
            <div className="text-xs font-medium text-muted-foreground mb-1">Display name (optional)</div>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Defaults to legal name"
              maxLength={200}
              className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
            />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs font-medium text-muted-foreground mb-1">Reliability (1-5)</div>
              <input
                type="number"
                min={1}
                max={5}
                value={reliabilityRating}
                onChange={(e) => setReliabilityRating(e.target.value === '' ? '' : Number(e.target.value))}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
              />
            </label>
            <label className="block">
              <div className="text-xs font-medium text-muted-foreground mb-1">Currency</div>
              <select
                value={preferredCurrency}
                onChange={(e) => setPreferredCurrency(e.target.value)}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="CAD">CAD</option>
                <option value="JPY">JPY</option>
                <option value="AUD">AUD</option>
                <option value="CHF">CHF</option>
                <option value="AED">AED</option>
                <option value="SGD">SGD</option>
              </select>
            </label>
          </div>
          <label className="block">
            <div className="text-xs font-medium text-muted-foreground mb-1">Payment terms</div>
            <input
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
              placeholder="Net 30 / CIA / etc."
              maxLength={200}
              className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
            />
          </label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={aogCapable}
                onChange={(e) => setAogCapable(e.target.checked)}
              />
              AOG capable
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={brokerFriendly}
                onChange={(e) => setBrokerFriendly(e.target.checked)}
              />
              Broker-friendly
            </label>
          </div>
          <label className="block">
            <div className="text-xs font-medium text-muted-foreground mb-1">Notes</div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={2000}
              className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
            />
          </label>

          {createMutation.isError && (
            <div className="text-sm text-destructive">
              {(createMutation.error as { message?: string })?.message ?? 'Save failed'}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border flex-shrink-0">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md text-muted-foreground hover:bg-muted">
            Cancel
          </button>
          <button
            onClick={() => createMutation.mutate()}
            disabled={!legalName.trim() || createMutation.isPending}
            className="px-3 py-1.5 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
          >
            {createMutation.isPending ? 'Creating…' : 'Create vendor'}
          </button>
        </div>
      </div>
    </div>
  );
}
